import { getToken } from "utils/helper";
import { apiSrcUrl } from "../ApiRoutes";
import { GetUsersQuery, User, UserLite, UserWithPromoCode } from "utils/types";
import { CustomApiResponse } from "utils/helper/ApiResponses";
import { ApiService } from "../apiservices";

export async function postUserDetails<T = any>(
  object: any,
  route: string,
  userID: string
): Promise<CustomApiResponse<T>> {
  const token = getToken();
  if (!token) {
    throw new Error("Token manquant");
  }

  let body: any;
  switch (route) {
    case "promocode-apply":
      body = {
        promoCode: object.promoCode,
        totalWithoutDiscount: object.totalWithoutDiscount,
      };
      break;
    case "proceed-transaction":
      body = {
        item: object.item,
        totalTransaction: object.totalTransaction,
        promoCode: object.promoCode,
        promoCodeType: object.promoCodeType,
        isPromoCodeUsed: object.isPromoCodeUsed,
      };
      break;
    case "save-cart":
      body = { cart: object.cart };
      break;
    case "get-cart":
      body = {};
      break;
    case "clear-cart":
      body = {};
      break;
    case "is-username-unique":
      body = { username: object.username };
      break;
    case "transactions-by-timestamp":
      body = { timestamp: object.timestamp };
      break;
    default:
      body = object;
  }

  const url = `${apiSrcUrl}/private/users/${userID}/${route}`;

  try {
    const response = await ApiService.post<CustomApiResponse<T>>(url, body, {
      Authorization: `Bearer ${token}`,
    });

    const data: CustomApiResponse<T> = await response.data;
    if (!response.status) {
      throw new Error(data.message || data.status || "Erreur inconnue");
    }

    return data;
  } catch (error: any) {
    console.error(`Erreur API sur ${url} :`, error);
    throw new Error(error.message || "Erreur inconnue");
  }
}

export const getUsers = async ({
  page = 1,
  limit = 25,
  search = "",
  role = "",
  sortBy = "username",
  order = "asc",
  country = "",
}: GetUsersQuery): Promise<
  CustomApiResponse<{ data: UserWithPromoCode[]; meta: any }>
> => {
  const token = getToken();
  if (!token) throw new Error("Token manquant");

  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sortBy,
    order,
  });

  if (search) params.append("search", search);
  if (role) params.append("role", role);
  if (country) params.append("country", country);
  
  const url = `${apiSrcUrl}/private/users?${params.toString()}`;

  const response = await ApiService.get<
    CustomApiResponse<{ data: UserWithPromoCode[]; meta: any }>
  >(url, {
    Authorization: `Bearer ${token}`,
  });

  return response.data;
};

export const getUserByID = async (userID: string): Promise<User | null> => {
  const token = getToken();
  if (!token) {
    console.error("Token is missing");
    return null;
  }

  try {
    const response = await ApiService.get<CustomApiResponse<User>>(
      `${apiSrcUrl}/private/users/${userID}`,
      {
        Authorization: `Bearer ${token}`,
      }
    );

    if (
      response.status === 200 &&
      response.data.status === "success" &&
      response.data.data
    ) {
      return response.data.data;
    }

    console.warn("User fetch failed:", response.data.message);
    return null;
  } catch (error) {
    console.error("Error getting user:", error);
    return null;
  }
};

export const getUserDetails = async (
  userID: string,
  type: string
): Promise<any | null> => {
  const token = getToken();
  if (!token) throw new Error("Token manquant");

  const { data } = await ApiService.get<CustomApiResponse<any>>(
    `${apiSrcUrl}/private/users/${userID}/${type}`,
    {
      Authorization: `Bearer ${token}`,
    }
  );

  return data?.data ?? null;
};

export const updateUser = async (
  userID: string,
  user: User,
  token: string | null
): Promise<void> => {
  if (!token) {
    throw new Error(`Failed to retrieve the token`);
  }
  const response = await fetch(`${apiSrcUrl}/private/users/${userID}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(user),
  });

  if (!response.ok) {
    throw new Error(`Error updating user: ${response.statusText}`);
  }
};

export const UpdateUserDetails = async (
  object: any,
  route: string,
  userID: string,
  token: string | null
): Promise<any> => {
  if (!token) {
    throw new Error("Failed to post provider");
  }
  let body: any;
  let headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
  };

  switch (route) {
    case "amount":
      body = JSON.stringify({
        amount: object.amount,
        userIDAdmin: object.userIDAdmin,
        userIDUpdateUser: object.userIDUpdateUser,
      });
      headers["Content-Type"] = "application/json";
      break;
    case "role":
      body = JSON.stringify({
        role: object.role,
        userIDAdmin: object.userIDAdmin,
        userIDUpdateUser: object.userIDUpdateUser,
      });
      headers["Content-Type"] = "application/json";
      break;
    default:
      body = JSON.stringify(object);
      headers["Content-Type"] = "application/json";
      break;
  }
  const response = await fetch(
    `${apiSrcUrl}/private/users/${userID}/${route}`,
    {
      method: "PUT",
      headers: headers,
      body: body,
    }
  );

  if (!response.ok) {
    throw new Error("Failed to post provider");
  }

  const data = await response.json();
  return data;
};

export const deleteUser = async (userID: string): Promise<void> => {
  const token = getToken();
  const response = await fetch(`${apiSrcUrl}/private/users/${userID}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Error deleting user: ${response.statusText}`);
  }
};

/**
 * Updates a user's details by an admin.
 * Used for changing roles and assigning countries.
 * @param userId - The ID of the user to update.
 * @param payload - The data to update (e.g., role, country array).
 * @returns The response from the API.
 */
export const updateUserByAdmin = async (
  userId: string,
  payload: Partial<User>
): Promise<any> => {
  if (!apiSrcUrl) {
    throw new Error("API source URL is not configured.");
  }
  const token = getToken();
  if (!token) {
    throw new Error("Authentication token not found.");
  }

  const url = `${apiSrcUrl}/private/users/${userId}`;

  // Using ApiService.patch to send the request
  const response = await ApiService.patch<any>(url, payload, {
    Authorization: `Bearer ${token}`,
  });

  return response.data;
};

/**
 * Fetches a list of all users. Admin only.
 * @returns A promise that resolves to an array of all users.
 */
export const getAllUsers = async (): Promise<User[]> => {
  const token = getToken();
  if (!token) {
    throw new Error("Authentication token not found.");
  }

  // Assuming you have a '/api/private/users' endpoint to get all users
  const url = `${apiSrcUrl}/private/users`;

  const response = await ApiService.get<CustomApiResponse<User[]>>(url, {
    Authorization: `Bearer ${token}`,
  });

  if (response.data.status !== "success" || !response.data.data) {
    throw new Error(response.data.message || "Failed to fetch users.");
  }

  return response.data.data;
};




export async function searchUsers(q: string, page = 1, limit = 10) {
  const token = getToken();
  const url = `${apiSrcUrl}/private/users?search=${encodeURIComponent(q)}&page=${page}&limit=${limit}&sortBy=username&order=asc`;
  const { data } = await ApiService.get<
    CustomApiResponse<{ data: UserLite[]; meta: any }>
  >(url, { Authorization: `Bearer ${token}` });
  return data;
}